import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { ButtonAction, ScreenName } from '@heidi/analytics/vocabulary';
import { type ComplianceNotificationType } from '@heidi/growth';

/**
 * The app-side emit host for the meddevice compliance-notification funnel
 * (MOD-1618) and the two settings toggles the banner drives.
 *
 * Ruling 24: `@heidi/growth` decides WHEN each banner event fires and supplies
 * `notification.type` (plus, for the impression, whether the row was pinned);
 * this module owns the mapping onto the existing `modal_viewed` /
 * `button_clicked` events. No bespoke schema — this banner is one more
 * "prominent surface shown" / "button clicked" case. Placement is kept on
 * the port so a follow-up can emit `is_pinned` once it exists on the
 * `modal_viewed` Iglu schema; it is not sent in this PR.
 *
 * MODULE-LEVEL FUNCTIONS ON PURPOSE. `onViewed` is a dependency of an
 * impression effect in the card, so an emit whose identity changed per render
 * would re-fire it. A module binding has one identity for the life of the
 * process. The ports object below is the same constant on every render for the
 * same reason.
 *
 * ROUTING: `{ amplitude: false }` — Snowplow + Datadog, not Amplitude. These
 * reuse existing event names, so Amplitude would otherwise stay on by default.
 */

const SCREEN_NAME_BY_TYPE = {
  mfa_setup: ScreenName.ComplianceNotificationMfaSetup,
  inactivity_logout: ScreenName.ComplianceNotificationInactivityLogout,
} as const satisfies Record<ComplianceNotificationType, ScreenName>;

export const screenNameFor = (type: ComplianceNotificationType): ScreenName =>
  SCREEN_NAME_BY_TYPE[type];

/** Fire once per (type, pinned) placement when the row is shown. */
export const trackComplianceNotificationViewed = (
  type: ComplianceNotificationType,
  pinned: boolean
) => {
  // Placement stays on the port for the Iglu follow-up; do not emit `is_pinned` yet.
  void pinned;
  track({
    event_name: TrackingEventName.ModalViewed,
    props: { screen_name: screenNameFor(type) },
    options: { amplitude: false },
  });
};

/** Fire when the user opens the row (navigates to the recommended setting). */
export const trackComplianceNotificationClicked = (type: ComplianceNotificationType) => {
  track({
    event_name: TrackingEventName.ButtonClicked,
    props: {
      screen_name: screenNameFor(type),
      action: ButtonAction.ComplianceNotificationClicked,
      label: 'notification_row',
    },
    options: { amplitude: false },
  });
};

/** Fire when the user deletes the row. */
export const trackComplianceNotificationDismissed = (
  type: ComplianceNotificationType
) => {
  track({
    event_name: TrackingEventName.ButtonClicked,
    props: {
      screen_name: screenNameFor(type),
      action: ButtonAction.ComplianceNotificationDismissed,
      label: 'notification_delete',
    },
    options: { amplitude: false },
  });
};

// trackSettingsToggleEnabled and SettingsToggleEnabledProps moved to @heidi/workspace/data — history: git log --follow src/models/analytics/features/trackComplianceNotificationAnalytics.ts

/**
 * The three seams as one bundle, for the two surfaces that compose the card
 * (`NotificationsList` / `PinnedComplianceNotificationsSection`).
 *
 * A module constant rather than a `useMemo`, so it is the same object on every
 * render and cannot be the thing that re-fires an impression effect.
 */
export const complianceNotificationAnalyticsPorts = {
  onViewed: trackComplianceNotificationViewed,
  onClicked: trackComplianceNotificationClicked,
  onDismissed: trackComplianceNotificationDismissed,
};
